'use client';

import { useState, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { uploadMediaFile, deleteMediaFile, StorageMediaItem, PortfolioBucket } from '@/lib/portfolioStorage';
import { useAuth } from '@/contexts/AuthContext';

interface AdminUploadPanelProps {
  bucket: PortfolioBucket;
  categoryLabel: string;
  items: StorageMediaItem[];
  onUploadComplete: (item: StorageMediaItem) => void;
  onDeleteComplete: (itemId: string) => void;
}

export default function AdminUploadPanel({
  bucket,
  categoryLabel,
  items,
  onUploadComplete,
  onDeleteComplete,
}: AdminUploadPanelProps) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!user) return null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    setUploadError(null);

    for (const file of files) {
      const result = await uploadMediaFile(bucket, file);
      if (result) {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const videoExts = ['mp4', 'webm', 'ogg', 'mov'];
        const newItem: StorageMediaItem = {
          id: result.name,
          name: result.name,
          url: result.url,
          type: videoExts.includes(ext) ? 'video' : 'image',
          bucket,
          createdAt: new Date().toISOString(),
        };
        onUploadComplete(newItem);
      } else {
        setUploadError(`Failed to upload ${file.name}. Please try again.`);
      }
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (item: StorageMediaItem) => {
    setDeletingId(item.id);
    const success = await deleteMediaFile(bucket, item.name);
    if (success) {
      onDeleteComplete(item.id);
    }
    setDeletingId(null);
  };

  const acceptTypes =
    bucket === 'content-creation' ?'image/*,video/*'
      : 'image/*';

  return (
    <div className="mt-6 border border-brand-teal/30 rounded-lg overflow-hidden">
      {/* Upload Header */}
      <div className="bg-brand-teal/5 border-b border-brand-teal/20 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="ShieldCheckIcon" size={16} className="text-brand-teal" />
          <span className="font-body font-semibold text-brand-plum text-sm">
            Admin — {categoryLabel}
          </span>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-teal text-white font-body font-semibold text-xs rounded hover:bg-brand-teal/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <>
              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Uploading…
            </>
          ) : (
            <>
              <Icon name="ArrowUpTrayIcon" size={14} />
              Upload Files
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptTypes}
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Error */}
      {uploadError && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center gap-2">
          <Icon name="ExclamationCircleIcon" size={14} className="text-red-500 flex-shrink-0" />
          <p className="font-body text-xs text-red-600">{uploadError}</p>
        </div>
      )}

      {/* Uploaded Files List */}
      {items.length > 0 && (
        <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {items.map((item) => (
            <div key={item.id} className="relative group rounded overflow-hidden bg-gray-100 aspect-video">
              {item.type === 'video' ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <Icon name="PlayCircleIcon" size={28} className="text-white/70" />
                  <span className="absolute bottom-1 left-1 right-1 text-white text-[10px] font-body truncate px-1">
                    {item.name.replace(/^\d+_/, '')}
                  </span>
                </div>
              ) : (
                <img
                  src={item.url}
                  alt={item.name.replace(/^\d+_/, '')}
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={() => handleDelete(item)}
                disabled={deletingId === item.id}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                title="Delete file"
              >
                {deletingId === item.id ? (
                  <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <Icon name="TrashIcon" size={12} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && !uploading && (
        <div className="px-4 py-3">
          <p className="font-body text-xs text-text-secondary">
            No files uploaded yet. Click "Upload Files" to add media to this category.
          </p>
        </div>
      )}
    </div>
  );
}
